/**
 * Tiny app-wide "undo" store. A destructive action pushes an entry; the
 * <UndoBanner /> in AppShell renders it for `ttlMs` and calls `onUndo` when
 * the user clicks Undo. Module-scoped (not React context) so the banner
 * survives the navigation that usually follows a delete — the component
 * that triggered the delete is already unmounted by then.
 */
import { useSyncExternalStore } from 'react';

export interface UndoEntry {
  id: number;
  message: string;
  undoLabel: string;
  /** Resolve with an optional confirmation message ("Booking restored"). */
  onUndo: () => Promise<string | undefined>;
  expiresAt: number;
}

type Listener = () => void;

let current: UndoEntry | null = null;
let counter = 0;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

export function pushUndo(input: {
  message: string;
  onUndo: UndoEntry['onUndo'];
  undoLabel?: string;
  ttlMs?: number;
}): number {
  const id = ++counter;
  current = {
    id,
    message: input.message,
    undoLabel: input.undoLabel ?? 'Undo',
    onUndo: input.onUndo,
    expiresAt: Date.now() + (input.ttlMs ?? 10_000),
  };
  emit();
  return id;
}

export function dismissUndo(id?: number): void {
  if (current && (id === undefined || current.id === id)) {
    current = null;
    emit();
  }
}

export function getUndo(): UndoEntry | null {
  return current;
}

export function subscribeUndo(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useUndo(): UndoEntry | null {
  return useSyncExternalStore(subscribeUndo, getUndo, () => null);
}

/** Test helper — reset module state between cases. */
export function __resetUndoForTests(): void {
  current = null;
  counter = 0;
  emit();
}
